import React, { useEffect, useState } from "react";
import Grid from "@mui/material/Grid";

import Alert from "@mui/material/Alert";
import AlertTitle from "@mui/material/AlertTitle";
import Container from "@mui/material/Container";
import Link from "@mui/material/Link";

import Accordion from "@mui/material/Accordion";
import AccordionSummary from "@mui/material/AccordionSummary";
import AccordionDetails from "@mui/material/AccordionDetails";

import { useJwtStorage, useLocalStorage } from "../../hooks/useLocalStorage";
import { useNavigate } from "react-router-dom";

import Paper from "@mui/material/Paper";
import IconButton from "@mui/material/IconButton";
import Button from "@mui/material/Button";
import Chip from "@mui/material/Chip";
import Tooltip from "@mui/material/Tooltip";

import { ReactComponent as MoreHorizIcon } from "../../icons/MoreHoriz24.svg";
import { ReactComponent as ThumbUpIcon } from "../../icons/ThumbUp24.svg";
import { ReactComponent as ThumbDownIcon } from "../../icons/ThumbDown24.svg";

import { ReactComponent as ErrorIcon } from "../../icons/Error24.svg";
import { ReactComponent as TimerIcon } from "../../icons/Timer24.svg";
import { ReactComponent as SyncIcon } from "../../icons/Sync24.svg";
import { ReactComponent as ExpandMoreIcon } from "../../icons/ExpandMore24.svg";

import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableHead from "@mui/material/TableHead";
import TableContainer from "@mui/material/TableContainer";
import { isMobile } from "react-device-detect";

import Tabs from "@mui/material/Tabs";
import Tab from "@mui/material/Tab";

import DialogTitle from "@mui/material/DialogTitle";
import Dialog from "@mui/material/Dialog";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";

import dayjs from "dayjs";

import Title from "../../components/Title";
import DrawerAppBar from "../../components/DrawerAppBar";
import AdminMenu from "../../components/admin/AdminMenu";
import ProgressBar from "../../components/ProgressBar";

import { doService } from "../../services/ServiceHelper";
import {
  getDashboardSummaries,
  getCheckinSummaries,
  getEntityTypeText,
  getActionText,
} from "../../services/ReportService";
import {
  StripedTableCell,
  StripedTableHeadCell,
  StripedTableRow,
} from "../../components/StripedTable";
import { getPeriodName } from "../../services/SchoolService";
import TableRowNoResults from "../../components/TableRowNoResults";
import {
  getSchoolDidNotReturnPasses,
  getAllStudentPasses,
  getPassStatusText,
} from "../../services/PassService";
import { getPriorityText } from "../../services/UserService";
import SubTitle from "../../components/SubTitle";
import HeaderText from "../../components/HeaderText";
import isEmpty from "lodash-es/isEmpty";

import { MobileDateRangePicker } from "@mui/x-date-pickers-pro/MobileDateRangePicker";
import InputLabel from "@mui/material/InputLabel";
import { default as SingleSelect } from "@mui/material/Select";
import { default as SingleMenuItem } from "@mui/material/MenuItem";
import TextField from "@mui/material/TextField";

import StudentsSelector from "../../components/faculty/report/StudentsSelector";
import {
  adminAddCheckinComment,
  adminUpdateScoreTime,
} from "../../services/ScoreService";
import {
  getReportScoreMetrics,
  getWatchdogFeed,
} from "../../services/ReportService";
import { showToast } from "../../components/ATPDialogs";
import {
  getOpenSchoolPasses,
  approvePass,
  rejectPass,
  returnPass,
  returnLatePass,
  didNotReturnPass,
} from "../../services/PassService";
import {
  isScoreEnabled,
  isCheckinEnabled,
  isPassEnabled,
  isActivitiesEnabled,
} from "../../services/SchoolService";
import PaginatedTable from "../../components/PaginatedTable";
import MyVerticalBarChart from "../../components/chart/MyVerticalBarChart";
import MyLineChart from "../../components/chart/MyLineChart";
import SearchBar from "../../components/faculty/SearchBar";
import UpdateScoreTimeDialog from "../../components/faculty/report/UpdateScoreTimeDialog";

const COLOR_POOL = [
  "#d32f2f",
  "#0288d1",
  "#252850",
  "#317F43",
  "#6C7059",
  "#641C34",
  "#A18594",
  "#828282",
  "#193737",
  "#F75E25",
];

const ADMIN_WELCOME_TABS = {
  messages: "messages",
  activity: "activity",
  activityFeed: "activityFeed",
  score: "score",
  pass: "pass",
};

const DEFAULT_ADMIN_HOME_TAB_ORDER =
  "messages,activity,activityFeed,score,pass";

const getAdminHomeTabOrder = (school) => {
  const requestedTabOrder = (
    school?.adminHomeTabOrder || DEFAULT_ADMIN_HOME_TAB_ORDER
  )
    .split(",")
    .map((tab) => tab.trim())
    .filter((tab) =>
      [
        ADMIN_WELCOME_TABS.messages,
        ADMIN_WELCOME_TABS.activity,
        ADMIN_WELCOME_TABS.activityFeed,
        ADMIN_WELCOME_TABS.score,
        ADMIN_WELCOME_TABS.pass,
      ].includes(tab),
    );

  const orderedTabValues = [...new Set(requestedTabOrder)];

  if (orderedTabValues.length === 0) {
    orderedTabValues.push(...DEFAULT_ADMIN_HOME_TAB_ORDER.split(","));
  }

  return orderedTabValues;
};

const getAdminTabConfig = (school, tabValue) => {
  const config = {
    [ADMIN_WELCOME_TABS.messages]: {
      label: "Messages",
      disabled: !isActivitiesEnabled(school) && !isPassEnabled(school),
    },
    [ADMIN_WELCOME_TABS.activity]: {
      label: "Activities",
      disabled: !isActivitiesEnabled(school),
    },
    [ADMIN_WELCOME_TABS.activityFeed]: {
      label: "Activity Feed",
      disabled: !isActivitiesEnabled(school),
    },
    [ADMIN_WELCOME_TABS.score]: {
      label: "Scores",
      disabled: !isScoreEnabled(school),
    },
    [ADMIN_WELCOME_TABS.pass]: {
      label: "Passes",
      disabled: !isPassEnabled(school),
    },
  };

  return config[tabValue];
};

const AdminWelcome = () => {
  const navigate = useNavigate();

  const [_school] = useLocalStorage("Auth", "school", null);
  const [_jwt] = useJwtStorage();
  const orderedTabValues = getAdminHomeTabOrder(_school);
  const defaultTabValue = orderedTabValues[0] || ADMIN_WELCOME_TABS.messages;
  const [_selectedTabValue, setSelectedTabValue] = useState(defaultTabValue);
  const activeTabValue = orderedTabValues.includes(_selectedTabValue)
    ? _selectedTabValue
    : defaultTabValue;
  const [_showProgress, setShowProgress] = useState(false);

  const [_activityWarnings, setActivityWarnings] = useState([]);
  const [_scoreWarnings, setScoreWarnings] = useState([]);
  const [_activityInfos, setActivityInfos] = useState([]);
  const [_passesDnr, setPassesDnr] = useState([]);

  const [_selectedPass, setSelectedPass] = useState({});
  const [_passDetailsOpen, setPassDetailsOpen] = useState(false);
  const [_passHistoryOpen, setPassHistoryOpen] = useState(false);
  const [_selectedStudentPasses, setSelectedStudentPasses] = React.useState([]);
  const [_selectCutoffDateIssue, setSelectCutoffDateIssue] = useState("");
  const [_periodSummaries, setPeriodSummaries] = useState({
    todayStudentCount: [],
    tomorrowStudentCount: [],
  });
  const [_todayActivityDailyCounts, setTodayActivityDailyCounts] = useState([]);
  const [_todayActivityStudentCounts, setTodayActivityStudentCounts] = useState(
    [],
  );
  const [_tomorrowActivityStudentCounts, setTomorrowActivityStudentCounts] =
    useState([]);
  const [_studentCount, setStudentCount] = useState([]);
  const [_todayActivityCategoryCounts, setTodayActivityCategoryCounts] =
    useState([]);

  const [_dailyActivityCountMeta, setDailyActivityCountMeta] = useState({
    labels: [],
    series: [],
  });
  const [_teachersWithoutActivitiesMeta2, setTeachersWithoutActivitiesMeta2] =
    useState({
      labels: [],
      series: [],
    });
  const [_checkinCheckoutHistory, setCheckinCheckoutHistory] = useState([]);
  const [_studentsCheckedIn, setStudentsCheckedIn] = useState([]);
  const [_studentCommentOpen, setStudentCommentOpen] = useState(false);
  const [_dateRange, setDateRange] = useState([dayjs(), dayjs()]);
  const [_selectedStudentEids, setSelectedStudentEids] = useState([]);
  const [_commentDetails, setCommentDetails] = useState("");
  const [_metaSchoolComments, setMetaSchoolComments] = useState([]);
  const [_schoolCommentId, setSchoolCommentId] = useState("1");
  const [_openPasses, setOpenPasses] = useState([]);
  const [_scoreCategoryCounts, setScoreCategoryCounts] = useState([]);
  const [_totalCategoryCount, setTotalCategoryCount] = useState(0);
  const [_trendingScoreData, setTrendingScoreData] = useState({});
  const [_watchdogFeed, setWatchdogFeed] = useState([]);
  const [_showWatchdogFeedProgress, setShowWatchdogFeedProgress] =
    useState(false);
  const [_isPassExpanded, setIsPassExpanded] = useState(true);
  const [_isScoreExpanded, setIsScoreExpanded] = useState(true);
  const [_isActivityExpanded, setIsActivityExpanded] = useState(true);
  const [_isActivityStatusExpanded, setIsActivityStatusExpanded] =
    useState(true);
  const [_studentSignupStatements, setStudentSignupStatements] = useState([]);
  const [_teacherEditStatements, setTeacherEditStatements] = useState([]);
  const [_timeOpen, setTimeOpen] = useState(false);
  const [_updatedTime, setUpdatedTime] = useState(null);
  const [_selectedScore, setSelectedScore] = useState({});

  const toggleActivityStatusExpanded = () => {
    setIsActivityStatusExpanded((prev) => !prev);
  };

  const toggleActivityExpanded = () => {
    setIsActivityExpanded((prev) => !prev);
  };

  const toggleResultsExpanded = () => {
    setIsPassExpanded((prev) => !prev);
  };

  const toggleScoreExpanded = () => {
    setIsScoreExpanded((prev) => !prev);
  };

  useEffect(() => {
    initDashboardMessages();
    initDidNotReturnPasses();

    if (isCheckinEnabled(_school)) {
      initCheckins();
      setSelectedTabValue(ADMIN_WELCOME_TABS.activityFeed);

      setInterval(() => {
        initCheckins();
      }, 60000);
    }
    if (isPassEnabled(_school)) {
      initOpenPasses();
      initDidNotReturnPasses();
    }

    if (isScoreEnabled(_school)) {
      initTodayScoreDashboard();
    }

    if (isActivitiesEnabled(_school)) {
      initWatchdogFeed();
    }
  }, []);

  const initWatchdogFeed = () => {
    setShowWatchdogFeedProgress(true);
    doService(getWatchdogFeed(_school, _jwt), (results) => {
      setWatchdogFeed(results.data);
      setShowWatchdogFeedProgress(false);
    });
  };

  const initTodayScoreDashboard = () => {
    setShowProgress(true);
    const startDate = dayjs().format("YYYY-MM-DD");
    const endDate = dayjs().format("YYYY-MM-DD");
    doService(
      getReportScoreMetrics(_school, _jwt, startDate, endDate, "XX"),
      (results) => {
        setScoreCategoryCounts(results.data.countsByCategory);

        let count = 0;
        for (const category of results.data.countsByCategory) {
          count += parseInt(category.categoryCount);
        }
        setTotalCategoryCount(count);

        initTrendingScoreGraph(results.data.trendCountsByDate);
      },
    );
  };

  const initTrendingScoreGraph = (rows) => {
    const labels = [];
    const historicalData = [];
    const predictedData = [];
    for (const row of rows) {
      labels.push(dayjs(row.scoreDate).format("MM/DD"));

      if (row.predicted === true) {
        predictedData.push(row.dayOfWeekCount);
        historicalData.push(0);
      } else {
        predictedData.push(0);
        historicalData.push(row.dayOfWeekCount);
      }
    }

    setTrendingScoreData({
      labels: labels,
      series: [
        {
          name: "Historical Days",
          data: historicalData,
        },
        {
          name: "Predicted Days",
          data: predictedData,
        },
      ],
    });
  };

  const initOpenPasses = () => {
    setShowProgress(true);
    doService(getOpenSchoolPasses(_school, _jwt), (results) => {
      setOpenPasses(results.data);
      setShowProgress(false);
    });
  };

  const initCheckins = () => {
    setShowProgress(true);
    doService(getCheckinSummaries(_school, _jwt), (results) => {
      setStudentsCheckedIn(results.data.studentsCheckedIn);
      setCheckinCheckoutHistory(results.data.checkinCheckoutHistory);
      setMetaSchoolComments(results.data.metaSchoolComments);
      setShowProgress(false);
    });
  };

  const initDidNotReturnPasses = () => {
    setShowProgress(true);
    doService(getSchoolDidNotReturnPasses(_school, _jwt), (results) => {
      setPassesDnr(results.data);
      setShowProgress(false);
    });
  };

  const initDashboardMessages = () => {
    setShowProgress(true);
    doService(getDashboardSummaries(_school, _jwt), (results) => {
      const activities = results.data.activities;
      const scores = results.data.scores;

      const adjustedStudentCount =
        results.data.totalStudentCount -
        results.data.excludedDateStudentCount -
        results.data.excludedStudentCount;

      initDailyActivityCountsMeta(results.data.rangeActivityTotalCounts);
      initTotalSeatCountMeta(activities.messages, adjustedStudentCount);
      initTeachersWithoutActivitiesMeta(results.data.teachersWithoutActivities);

      if (activities.messages != null && activities.messages.warning != null) {
        setActivityWarnings(activities.messages.warning);
      }
      if (activities.messages != null && activities.messages.info != null) {
        setActivityInfos(activities.messages.info);
      }
      if (scores.messages != null && scores.messages.warning != null) {
        setScoreWarnings(scores.messages.warning);
      }

      setSelectCutoffDateIssue(results.data.selectCutoffDateIssue);
      setPeriodSummaries(results.data.periodSummaries);
      setTodayActivityDailyCounts(results.data.todayActivityDailyCounts);
      // setTodayActivityMaxCounts(results.data.todayActivityMaxCounts);
      setStudentCount(adjustedStudentCount);
      setTodayActivityStudentCounts(results.data.todayActivityStudentCounts);
      setTomorrowActivityStudentCounts(
        results.data.tomorrowActivityStudentCounts,
      );
      setTodayActivityCategoryCounts(results.data.todayActivityCategoryCounts);
      setStudentSignupStatements(results.data.studentSignupStatements);
      setTeacherEditStatements(results.data.teacherEditStatements);

      setShowProgress(false);
    });
  };

  const initTotalSeatCountMeta = (data, studentCount) => {
    const labels = [];
    const studentCountData = [];

    // format labels (dates) and init the student count line
    for (const entry of data.activitySeatCountLabels) {
      labels.push(dayjs(entry).format("M/D"));
      studentCountData.push(studentCount);
    }

    const datasets = [];

    // add the student counts line
    datasets.push({
      type: "line",
      label: "Student Count",
      borderColor: "black",
      borderWidth: 2,
      fill: false,
      data: studentCountData,
    });

    // lunch dataset
    datasets.push({
      type: "bar",
      label: "Lunches",
      backgroundColor: COLOR_POOL[0],
      data: data.activitySeatCountDataLunch,
      borderColor: "white",
      stack: "Stack 0",
    });

    // no lunch dataset
    datasets.push({
      type: "bar",
      label: "Not Lunches",
      backgroundColor: COLOR_POOL[1],
      data: data.activitySeatCountDataNoLunch,
      stack: "Stack 0",
    });

    // setTotalSeatCountMeta({
    //   labels,
    //   datasets: datasets,
    // });
  };

  /**
   *
   * @param {key: p1, value: obj of date, count} map
   */
  const initDailyActivityCountsMeta = (map) => {
    const labels = [];
    const datasets = [];

    let periodKeys = Object.keys(map);
    if (periodKeys.length > 0) {
      // get the x labels
      for (const entry of map["p1"]) {
        labels.push(dayjs(entry.date).format("M/D"));
      }
    }

    // loop each period and build a data set
    for (const periodKey of periodKeys) {
      const periodNum = parseInt(periodKey.substring(1)); // 1,2,3,etc.
      const dataset = { name: getPeriodName(_school, periodNum - 1) };

      const data = [];
      for (const entry of map[periodKey]) {
        data.push(entry.count);
      }
      dataset.data = data;
      datasets.push(dataset);
    }

    setDailyActivityCountMeta({
      labels: labels,
      series: datasets,
    });
  };

  /**
   *
   * @param {key: p1, value: obj of date, count} map
   */
  const initTeachersWithoutActivitiesMeta = (entries) => {
    const labels = [];
    const data = [];

    for (const entry of entries) {
      labels.push(dayjs(entry.date).format("M/D"));
      data.push(entry.count);
    }

    setTeachersWithoutActivitiesMeta2({
      labels: labels,
      series: [
        {
          name: "Teachers without Activities",
          data: data,
        },
      ],
    });
  };

  const onTabChange = (event, newValue) => {
    setSelectedTabValue(newValue);
  };

  const formatMessage = (message) => {
    let text = "";

    if (message.date != null) {
      text = dayjs(message.date).format("MM/DD") + " - ";
    }

    text += message.title;

    return (
      <>
        <b>{text}</b> <br />
        {message.detail}
      </>
    );
  };

  const getStatusChipFromStatements = (statements = []) => {
    const cleanStatements = statements.filter(
      (statement) => typeof statement === "string" && statement.length > 0,
    );

    const statusText = cleanStatements.some((statement) =>
      /\bENABLED\b/i.test(statement),
    )
      ? "ENABLED"
      : cleanStatements.some((statement) => /\bDISABLED\b/i.test(statement))
        ? "DISABLED"
        : "LOADING...";

    const chipColor = statusText === "ENABLED" ? "success" : "error";

    return {
      statusText,
      chipColor,
      tooltipContent: (
        <div style={{ whiteSpace: "pre-line" }}>
          {cleanStatements.map((statement, index) => (
            <div key={`${statement}-${index}`}>{statement}</div>
          ))}
        </div>
      ),
    };
  };

  const getPassDestinationText = (pass) => {
    let toHtml = "";

    if (pass.to_location_name != null) {
      toHtml = `${pass.to_location_name} (Location)`;
    } else {
      toHtml = `${pass.to_teacher_last_name}, ${pass.to_teacher_first_name} (Teacher)`;
    }
    return toHtml;
  };

  const getCommentText = (pass) => {
    let comment = "";
    if (pass.teacher_comment != null && pass.teacher_comment.length > 0) {
      comment = `TEACHER COMMENT: <span style="font-style:italic">${pass.teacher_comment}</span>`;
    }
    return comment;
  };

  const handlePassDetailsClick = (pass) => {
    setSelectedPass(pass);
    setPassDetailsOpen(true);
  };

  const handlePassDetailsClose = () => {
    setPassDetailsOpen(false);
  };

  const handlePassHistoryClose = () => {
    setPassHistoryOpen(false);
  };

  const handlePassHistoryClick = (pass) => {
    setSelectedPass(pass);
    setShowProgress(true);
    doService(
      getAllStudentPasses(_school, _jwt, pass.student_eid),
      (results) => {
        setShowProgress(false);
        setSelectedStudentPasses(results.data);
        setPassHistoryOpen(true);
      },
    );
  };

  const getActivityPercentText = (isToday, i) => {
    let pct = 0;

    if (isToday) {
      pct = (_todayActivityStudentCounts[i] * 100.0) / _studentCount;
    } else {
      pct = (_tomorrowActivityStudentCounts[i] * 100.0) / _studentCount;
    }

    pct = Math.min(pct, 100.0);
    return pct.toFixed(1) + "%";
  };

  const handleAddScore = () => {
    navigate("/teacher/score/add");
  };

  const handleAddComment = () => {
    setStudentCommentOpen(true);
  };

  const handleStudentCommentClose = () => {
    setStudentCommentOpen(false);
  };

  const handleAddCommentOK = () => {
    if (_selectedStudentEids == null || _selectedStudentEids.length === 0) {
      alert("Please select 1 or more students.");
      return;
    }

    const studentEids = [];
    for (const entry of _selectedStudentEids) {
      studentEids.push(entry.value);
    }

    const dateStart = _dateRange[0].format("YYYY-MM-DD");
    const dateEnd = _dateRange[1].format("YYYY-MM-DD");

    setShowProgress(true);
    doService(
      adminAddCheckinComment(
        _school,
        _jwt,
        dateStart,
        dateEnd,
        studentEids,
        _schoolCommentId,
        _commentDetails,
      ),
      (results) => {
        setShowProgress(false);
        setSelectedStudentEids([]);
        setSchoolCommentId("1");
        setCommentDetails("");
        showToast("S", "Comment Added!");
      },
    );
  };

  const handleChangeDateRange = (value) => setDateRange(value);
  const handleScoreDashboard = () => navigate("/report/score-dashboard");

  const formatPassFromTo = (pass) => {
    let teacherLastName = "";
    let teacherFirstName = "";
    let toLocationText = "";

    if (!isEmpty(pass.teacherLastName)) {
      teacherLastName = pass.teacherLastName;
      teacherFirstName = pass.teacherFirstName;
    } else if (!isEmpty(pass.teacher_last_name)) {
      teacherLastName = pass.teacher_last_name;
      teacherFirstName = pass.teacher_first_name;
    }

    if (pass.toLocationName == null && pass.toTeacherLastName != null) {
      toLocationText = `${
        pass.toTeacherLastName
      }, ${pass.toTeacherFirstName.substring(0, 1)}`;
    } else if (pass.toLocationName != null) {
      toLocationText = pass.toLocationName;
    } else if (pass.toLocationName == null && pass.toTeacherLastName == null) {
      toLocationText = "N/A";
    }

    return (
      <>
        FROM: {teacherLastName}, {teacherFirstName.substring(0, 1)} <br />
        TO: {toLocationText}
      </>
    );
  };

  const handleApprovePass = (pass) => {
    doService(approvePass(_school, _jwt, pass.eid), (result) => {
      initOpenPasses();
    });
  };

  const handleRejectPass = (pass) => {
    const comment = window.prompt("Please enter comment (optional).");

    doService(rejectPass(_school, _jwt, pass.eid, comment), (result) => {
      initOpenPasses();
    });
  };

  const handleReturnPass = (pass) => {
    doService(returnPass(_school, _jwt, pass.eid), (result) => {
      initOpenPasses();
    });
  };

  const handleReturnLatePass = (pass) => {
    doService(returnLatePass(_school, _jwt, pass.eid), (result) => {
      initOpenPasses();
    });
  };

  const handleDnrPass = (pass) => {
    doService(didNotReturnPass(_school, _jwt, pass.eid), (result) => {
      initOpenPasses();
    });
  };

  const handleTimeClose = () => {
    setTimeOpen(false);
  };

  const onTimeClickOk = () => {
    const newDateTime = _updatedTime.format("YYYY-MM-DD HH:mm:00");

    setShowProgress(true);
    setTimeOpen(false);
    doService(
      adminUpdateScoreTime(_school, _jwt, _selectedScore.eid, newDateTime),
      (result) => {
        setShowProgress(false);
        showToast("S", "Score Time Updated");
        initCheckins();
      },
    );
  };

  const getPassActionButtons = (pass) => {
    if (pass.status === 0) {
      return (
        <>
          <IconButton
            aria-label="approve pass"
            sx={{
              color: "white",
              backgroundColor: "green",
            }}
            size="small"
            title="approve pass"
            onClick={() => handleApprovePass(pass)}
          >
            <ThumbUpIcon />
          </IconButton>
          <IconButton
            aria-label="reject pass"
            sx={{
              color: "white",
              backgroundColor: "red",
              ml: 0.5,
            }}
            size="small"
            title="reject pass"
            onClick={() => handleRejectPass(pass)}
          >
            <ThumbDownIcon />
          </IconButton>
        </>
      );
    } else if (pass.status === 50 || pass.status === 700) {
      return (
        <>
          <IconButton
            aria-label="RETURNED ON TIME"
            sx={{
              color: "white",
              backgroundColor: "blue",
            }}
            size="small"
            title="RETURNED ON TIME"
            onClick={() => handleReturnPass(pass)}
          >
            <SyncIcon />
          </IconButton>{" "}
          <IconButton
            aria-label="RETURNED LATE"
            sx={{
              color: "white",
              backgroundColor: "orange",
              ml: 0.5,
            }}
            size="small"
            title="RETURNED LATE"
            onClick={() => handleReturnLatePass(pass)}
          >
            <TimerIcon />
          </IconButton>{" "}
          <IconButton
            aria-label="DID NOT RETURN"
            sx={{
              color: "white",
              backgroundColor: "red",
              ml: 0.5,
            }}
            size="small"
            title="DID NOT RETURN"
            onClick={() => handleDnrPass(pass)}
          >
            <ErrorIcon />
          </IconButton>{" "}
        </>
      );
    }
    return <>IDK</>;
  };

  const handleUpdateTime = (score) => {
    setUpdatedTime(dayjs(score.dateCreated));
    setSelectedScore(score);
    setTimeOpen(true);
  };

  const handleFixThis = (entry) => {
    navigate(entry.url);
  };

  return (
    <>
      <DrawerAppBar />
      <SearchBar />

      <Container sx={{ mt: 1, ml: 0 }}>
        <Grid container spacing={1}>
          {isMobile === false && (
            <Grid item sx={{ width: 200, maxWidth: 200 }} className="sidebar">
              <AdminMenu />
            </Grid>
          )}

          <Grid item sx={{ width: 950, maxWidth: "100%" }}>
            <Title text="ADMIN Dashboard" />
            <ProgressBar showProgress={_showProgress} />

            <Tabs value={activeTabValue} onChange={onTabChange}>
              {orderedTabValues.map((tabValue) => {
                const config = getAdminTabConfig(_school, tabValue);

                return (
                  <Tab
                    key={tabValue}
                    value={tabValue}
                    label={config.label}
                    disabled={config.disabled}
                    sx={{
                      borderRight: "1px solid #ccc",
                      minHeight: 48,
                    }}
                  />
                );
              })}
            </Tabs>

            <Container
              sx={{
                ml: 0,
                pl: 0,
                mr: 0,
                pr: 0,
                border: "1px solid #e8e8e8",
                width: 900,
                maxWidth: "100%",
              }}
            >
              <br />
              {activeTabValue === ADMIN_WELCOME_TABS.messages && (
                <>
                  {isPassEnabled(_school) && (
                    <Accordion
                      disableGutters
                      sx={{ boxShadow: 1, mt: 2, mb: 1, width: "100%" }}
                      defaultExpanded
                      expanded={_isPassExpanded === true}
                      onChange={() => toggleResultsExpanded()}
                    >
                      <AccordionSummary
                        className="accordionTitle accordionTitle-error"
                        expandIcon={<ExpandMoreIcon />}
                      >
                        Passes - Did not Return ({_passesDnr.length})
                      </AccordionSummary>
                      <AccordionDetails>
                        <TableContainer component={Paper} sx={{ mt: 1 }}>
                          <Table size="small">
                            <TableHead>
                              <StripedTableRow>
                                <StripedTableCell>Student</StripedTableCell>
                                <StripedTableCell>Type</StripedTableCell>
                                <StripedTableCell align="center">
                                  Actions
                                </StripedTableCell>
                              </StripedTableRow>
                            </TableHead>
                            <TableBody>
                              <TableRowNoResults data={_passesDnr} />
                              {_passesDnr.map((pass) => (
                                <StripedTableRow>
                                  <StripedTableCell>
                                    <Link
                                      component={"button"}
                                      onClick={() =>
                                        handlePassHistoryClick(pass)
                                      }
                                    >
                                      {pass.student_last_name},{" "}
                                      {pass.student_first_name}
                                    </Link>
                                  </StripedTableCell>
                                  <StripedTableCell>
                                    {pass.type_name}
                                  </StripedTableCell>
                                  <StripedTableCell align="center">
                                    <IconButton
                                      aria-label="pass details"
                                      color="inherit"
                                      sx={{
                                        backgroundColor: "rgb(253, 237, 237)",
                                      }}
                                      size="small"
                                      onClick={() =>
                                        handlePassDetailsClick(pass)
                                      }
                                    >
                                      <MoreHorizIcon />
                                    </IconButton>
                                  </StripedTableCell>
                                </StripedTableRow>
                              ))}
                            </TableBody>
                          </Table>
                        </TableContainer>
                      </AccordionDetails>
                    </Accordion>
                  )}

                  {isScoreEnabled(_school) && _scoreWarnings.length > 0 && (
                    <Accordion
                      disableGutters
                      sx={{ boxShadow: 1, mt: 2, pt: 2, mb: 1, width: "100%" }}
                      defaultExpanded
                      expanded={_isScoreExpanded === true}
                      onChange={() => toggleScoreExpanded()}
                    >
                      <AccordionSummary
                        className="accordionTitle accordionTitle-warning"
                        expandIcon={<ExpandMoreIcon />}
                      >
                        Score/Tardy Warnings ({_scoreWarnings.length})
                      </AccordionSummary>
                      <AccordionDetails>
                        <TableContainer component={Paper} sx={{ mt: 1 }}>
                          <Table size="small">
                            <TableHead>
                              <StripedTableRow>
                                <StripedTableCell colSpan={2}>
                                  Message
                                </StripedTableCell>
                              </StripedTableRow>
                            </TableHead>
                            <TableBody>
                              <TableRowNoResults data={_scoreWarnings} />
                              {_scoreWarnings.map((warning) => (
                                <StripedTableRow>
                                  <StripedTableCell>
                                    {warning.title}
                                  </StripedTableCell>
                                  {isEmpty(warning.url) === false && (
                                    <StripedTableCell
                                      width={110}
                                      align="center"
                                    >
                                      <Button
                                        variant="outlined"
                                        color="error"
                                        onClick={() => handleFixThis(warning)}
                                      >
                                        FIX THIS
                                      </Button>
                                    </StripedTableCell>
                                  )}
                                </StripedTableRow>
                              ))}
                            </TableBody>
                          </Table>
                        </TableContainer>
                      </AccordionDetails>
                    </Accordion>
                  )}

                  {isActivitiesEnabled(_school) && (
                    <Accordion
                      disableGutters
                      sx={{
                        boxShadow: 1,
                        mt: 2,
                        pt: 0,
                        mb: 1,
                        width: "100%",
                      }}
                      defaultExpanded
                      expanded={_isActivityStatusExpanded === true}
                      onChange={() => toggleActivityStatusExpanded()}
                    >
                      <AccordionSummary
                        className="accordionTitle accordionTitle-info"
                        expandIcon={<ExpandMoreIcon />}
                      >
                        Activity Status
                      </AccordionSummary>
                      <AccordionDetails>
                        <TableContainer component={Paper} sx={{ mt: 1 }}>
                          <Table size="small">
                            <TableBody>
                              <StripedTableRow>
                                <StripedTableHeadCell width={180}>
                                  Student Signup Status
                                </StripedTableHeadCell>
                                <StripedTableCell>
                                  {(() => {
                                    const status = getStatusChipFromStatements(
                                      _studentSignupStatements,
                                    );

                                    return (
                                      <Tooltip
                                        title={status.tooltipContent}
                                        arrow
                                      >
                                        <Chip
                                          label={status.statusText}
                                          color={status.chipColor}
                                          size="small"
                                          sx={{
                                            fontWeight: 700,
                                            minWidth: 96,
                                          }}
                                        />
                                      </Tooltip>
                                    );
                                  })()}
                                </StripedTableCell>
                                <StripedTableHeadCell>
                                  Teacher Edit Status
                                </StripedTableHeadCell>
                                <StripedTableCell>
                                  {(() => {
                                    const status = getStatusChipFromStatements(
                                      _teacherEditStatements,
                                    );

                                    return (
                                      <Tooltip
                                        title={status.tooltipContent}
                                        arrow
                                      >
                                        <Chip
                                          label={status.statusText}
                                          color={status.chipColor}
                                          size="small"
                                          sx={{
                                            fontWeight: 700,
                                            minWidth: 96,
                                          }}
                                        />
                                      </Tooltip>
                                    );
                                  })()}
                                </StripedTableCell>
                              </StripedTableRow>
                            </TableBody>
                          </Table>
                        </TableContainer>
                      </AccordionDetails>
                    </Accordion>
                  )}

                  {isActivitiesEnabled(_school) &&
                    _activityWarnings.length > 0 && (
                      <Accordion
                        disableGutters
                        sx={{
                          boxShadow: 1,
                          mt: 2,
                          pt: 0,
                          mb: 1,
                          width: "100%",
                        }}
                        defaultExpanded
                        expanded={_isActivityExpanded === true}
                        onChange={() => toggleActivityExpanded()}
                      >
                        <AccordionSummary
                          className="accordionTitle accordionTitle-warning"
                          expandIcon={<ExpandMoreIcon />}
                        >
                          Activity Warnings ({_activityWarnings.length})
                        </AccordionSummary>
                        <AccordionDetails>
                          <TableContainer component={Paper} sx={{ mt: 1 }}>
                            <Table size="small">
                              <TableHead>
                                <StripedTableRow>
                                  <StripedTableCell colSpan={2}>
                                    Message
                                  </StripedTableCell>
                                </StripedTableRow>
                              </TableHead>
                              <TableBody>
                                <TableRowNoResults data={_activityWarnings} />
                                {_activityWarnings.map((warning) => (
                                  <StripedTableRow>
                                    <StripedTableCell>
                                      {formatMessage(warning)}
                                    </StripedTableCell>
                                    {isEmpty(warning.url) === false && (
                                      <StripedTableCell
                                        width={110}
                                        align="center"
                                      >
                                        <Button
                                          variant="outlined"
                                          color="error"
                                          onClick={() => handleFixThis(warning)}
                                        >
                                          FIX THIS
                                        </Button>
                                      </StripedTableCell>
                                    )}
                                  </StripedTableRow>
                                ))}
                              </TableBody>
                            </Table>
                          </TableContainer>
                        </AccordionDetails>
                      </Accordion>
                    )}
                </>
              )}
              {activeTabValue === ADMIN_WELCOME_TABS.activity && (
                <>
                  {!isEmpty(_selectCutoffDateIssue) && (
                    <Alert>
                      <AlertTitle>{_selectCutoffDateIssue}</AlertTitle>
                    </Alert>
                  )}
                  <Grid container spacing={2}>
                    <Grid item xs={6}>
                      <HeaderText text="Activity Summary" />
                      <TableContainer component={Paper} sx={{ mt: 1, mb: 2 }}>
                        <Table size="small">
                          <TableHead>
                            <StripedTableRow>
                              <StripedTableCell>Name</StripedTableCell>
                              <StripedTableCell align="center" width={40}>
                                #
                              </StripedTableCell>
                              <StripedTableCell align="center" width={40}>
                                %
                              </StripedTableCell>
                              <StripedTableCell align="center" width={40}>
                                Tmrw
                              </StripedTableCell>
                            </StripedTableRow>
                          </TableHead>
                          <TableBody>
                            <TableRowNoResults
                              data={_periodSummaries.todayStudentCount}
                            />
                            {_periodSummaries.todayStudentCount.map(
                              (todayEntry, i) => (
                                <StripedTableRow>
                                  <StripedTableCell>
                                    {getPeriodName(_school, i)}
                                  </StripedTableCell>
                                  <StripedTableCell align="center">
                                    {_todayActivityDailyCounts[i]}
                                  </StripedTableCell>
                                  <StripedTableCell align="center">
                                    {getActivityPercentText(true, i)}
                                  </StripedTableCell>
                                  <StripedTableCell align="center">
                                    {getActivityPercentText(false, i)}
                                  </StripedTableCell>
                                </StripedTableRow>
                              ),
                            )}
                          </TableBody>
                        </Table>
                      </TableContainer>
                    </Grid>
                    <Grid item xs={6}>
                      <HeaderText text="Category Seat Counts" />
                      <TableContainer component={Paper} sx={{ mt: 1, mb: 2 }}>
                        <Table size="small">
                          <TableHead>
                            <StripedTableRow>
                              <StripedTableCell>Name</StripedTableCell>
                              <StripedTableCell align="center">
                                Seats
                              </StripedTableCell>
                            </StripedTableRow>
                          </TableHead>
                          <TableBody>
                            <TableRowNoResults
                              data={_todayActivityCategoryCounts}
                            />
                            {_todayActivityCategoryCounts.map((cat) => (
                              <StripedTableRow>
                                <StripedTableCell>{cat.name}</StripedTableCell>
                                <StripedTableCell align="center">
                                  {cat.totalSeats}
                                </StripedTableCell>
                              </StripedTableRow>
                            ))}
                          </TableBody>
                        </Table>
                      </TableContainer>
                    </Grid>
                    <Grid item xs={6}>
                      <HeaderText
                        text={`${_school.activityPrefix} Counts`}
                      />
                      <MyLineChart
                        data={_dailyActivityCountMeta}
                        height={200}
                      />
                    </Grid>
                    <Grid item xs={6}>
                      <HeaderText
                        text={`Teachers without ${_school.activityPrefix} (Daily)`}
                      />
                      <MyLineChart
                        data={_teachersWithoutActivitiesMeta2}
                        height={200}
                      />
                    </Grid>
                  </Grid>
                </>
              )}

              {activeTabValue === ADMIN_WELCOME_TABS.activityFeed && (
                <>
                  <Alert severity="info" sx={{ mt: 2, pb: 0 }}>
                    <AlertTitle>
                      Activity Watchdog Feed ({_watchdogFeed.length})
                    </AlertTitle>
                  </Alert>

                  <ProgressBar showProgress={_showWatchdogFeedProgress} />

                  <TableContainer component={Paper} sx={{ mt: 1, mb: 2 }}>
                    <Table size="small">
                      <TableHead>
                        <StripedTableRow>
                          <StripedTableCell align="center" width={100}>
                            Time
                          </StripedTableCell>
                          <StripedTableCell>Entity</StripedTableCell>
                          <StripedTableCell>Detail</StripedTableCell>
                          <StripedTableCell align="center" width={60}>
                            Period
                          </StripedTableCell>
                        </StripedTableRow>
                      </TableHead>
                      <TableBody>
                        <TableRowNoResults data={_watchdogFeed} />
                        {_watchdogFeed.map((w) => (
                          <StripedTableRow>
                            <StripedTableCell align="center">
                              {dayjs(w.dateCreated).format("MM/DD hh:mm")}
                            </StripedTableCell>
                            <StripedTableCell>
                              {getEntityTypeText(w)} -{" "}
                              {getActionText(w.actionCode)}
                            </StripedTableCell>
                            <StripedTableCell>
                              <b>{w.activityName}</b>
                              <br />
                              {w.studentLastName != null && (
                                <>
                                  ST: {w.studentLastName}, {w.studentFirstName}
                                </>
                              )}
                            </StripedTableCell>
                            <StripedTableCell align="center">
                              {w.periodNum}
                            </StripedTableCell>
                          </StripedTableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </>
              )}

              {activeTabValue === ADMIN_WELCOME_TABS.score && (
                <>
                  {!isCheckinEnabled(_school) && (
                    <>
                      <Grid container spacing={1}>
                        <Grid item xs={6}>
                          <HeaderText text={`Today's Scores`} />
                        </Grid>
                        <Grid item xs={6} textAlign="right" sx={{ mt: 1 }}>
                          <Button
                            variant="contained"
                            className="button1"
                            onClick={handleAddScore}
                          >
                            Add Score
                          </Button>

                          <Button
                            sx={{ ml: 1 }}
                            variant="contained"
                            className="button2"
                            onClick={handleScoreDashboard}
                          >
                            Dashboard
                          </Button>
                        </Grid>
                      </Grid>

                      <TableContainer component={Paper} sx={{ mt: 1, mb: 2 }}>
                        <Table size="small">
                          <TableHead>
                            <StripedTableRow>
                              <StripedTableCell>
                                Score Category
                              </StripedTableCell>
                              <StripedTableCell align="center">
                                #
                              </StripedTableCell>
                            </StripedTableRow>
                          </TableHead>
                          <TableBody>
                            <TableRowNoResults data={_scoreCategoryCounts} />
                            {_scoreCategoryCounts.map((category) => (
                              <StripedTableRow>
                                <StripedTableCell>
                                  {category.categoryName}
                                </StripedTableCell>
                                <StripedTableCell align="center">
                                  {category.categoryCount}
                                </StripedTableCell>
                              </StripedTableRow>
                            ))}
                            <StripedTableRow
                              style={{ backgroundColor: "#F0F8FF" }}
                            >
                              <StripedTableHeadCell>Total</StripedTableHeadCell>
                              <StripedTableHeadCell align="center">
                                {_totalCategoryCount}
                              </StripedTableHeadCell>
                            </StripedTableRow>
                          </TableBody>
                        </Table>
                      </TableContainer>

                      <SubTitle text="Trending Counts by Day" />
                      <Alert severity="info">
                        This shows the previous 15 days of scores, and predicted
                        next 15 days. Predictions are based on algorithms keyed
                        on the days of the week.
                      </Alert>

                      <MyVerticalBarChart
                        data={_trendingScoreData}
                        rotateLabels={true}
                      />
                    </>
                  )}

                  {isCheckinEnabled(_school) && (
                    <Button
                      sx={{ mt: 2 }}
                      variant="contained"
                      className="button3"
                      onClick={handleAddComment}
                    >
                      Add Student Comment
                    </Button>
                  )}

                  {isCheckinEnabled(_school) && (
                    <>
                      <br />
                      <Grid container spacing={2} sx={{ mt: 2 }}>
                        <Grid item xs={4}>
                          <SubTitle
                            text={`Currently Checked In (${_studentsCheckedIn.length})`}
                          />
                          <TableContainer
                            component={Paper}
                            sx={{ mt: 1, mb: 2 }}
                          >
                            <Table size="small">
                              <TableHead>
                                <StripedTableRow>
                                  <StripedTableCell>
                                    Student Name
                                  </StripedTableCell>
                                </StripedTableRow>
                              </TableHead>
                              <TableBody>
                                <TableRowNoResults data={_studentsCheckedIn} />
                                {_studentsCheckedIn.map((student) => (
                                  <StripedTableRow>
                                    <StripedTableCell>
                                      {student.last_name}, {student.first_name}
                                    </StripedTableCell>
                                  </StripedTableRow>
                                ))}
                              </TableBody>
                            </Table>
                          </TableContainer>
                        </Grid>
                        <Grid item xs={8}>
                          <SubTitle
                            text={`Today's Checkin History (${_checkinCheckoutHistory.length})`}
                          />

                          <PaginatedTable
                            defaultRowsPerPage={25}
                            rows={_checkinCheckoutHistory}
                            columns={[
                              {
                                name: "Date",
                                align: "center",
                                render: (row) => (
                                  <>{dayjs(row.dateCreated).format("MM/DD")}</>
                                ),
                              },
                              {
                                name: "Time",
                                align: "center",
                                render: (row) => (
                                  <>
                                    <Link
                                      component="button"
                                      onClick={() => handleUpdateTime(row)}
                                    >
                                      {dayjs(row.dateCreated).format("hh:mm a")}
                                    </Link>
                                  </>
                                ),
                              },
                              {
                                name: "Student Name",
                                render: (row) => (
                                  <>
                                    {row.last_name}, {row.first_name}
                                  </>
                                ),
                              },
                              {
                                name: "Type",
                                fieldName: "scoreCategoryName",
                              },
                            ]}
                          />
                        </Grid>
                      </Grid>
                    </>
                  )}
                </>
              )}
              {activeTabValue === ADMIN_WELCOME_TABS.pass && (
                <>
                  <HeaderText text={`Open Passes (${_openPasses.length})`} />
                  <TableContainer component={Paper} sx={{ mt: 1, mb: 2 }}>
                    <Table size="small">
                      <TableHead>
                        <StripedTableRow>
                          <StripedTableCell>Date/Time</StripedTableCell>
                          <StripedTableCell>Student</StripedTableCell>
                          <StripedTableCell>Status</StripedTableCell>
                          <StripedTableCell>From/To</StripedTableCell>
                          <StripedTableCell align="center" width={120}>
                            Actions
                          </StripedTableCell>
                        </StripedTableRow>
                      </TableHead>
                      <TableBody>
                        <TableRowNoResults data={_openPasses} />
                        {_openPasses.map((pass) => (
                          <StripedTableRow>
                            <StripedTableCell>
                              {dayjs(pass.created_at).format("MM/DD hh:mm")}
                            </StripedTableCell>
                            <StripedTableCell>
                              <Link
                                component={"button"}
                                onClick={() => handlePassHistoryClick(pass)}
                              >
                                {pass.student_last_name},{" "}
                                {pass.student_first_name}
                              </Link>{" "}
                            </StripedTableCell>
                            <StripedTableCell>
                              {pass.statusText}
                            </StripedTableCell>
                            <StripedTableCell>
                              {formatPassFromTo(pass)}
                            </StripedTableCell>
                            <StripedTableCell align="center">
                              {getPassActionButtons(pass)}
                            </StripedTableCell>
                          </StripedTableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </>
              )}
            </Container>
          </Grid>
        </Grid>
      </Container>

      <Dialog onClose={handlePassDetailsClose} open={_passDetailsOpen}>
        <DialogTitle>Pass Details</DialogTitle>

        <DialogContent>
          <TableContainer component={Paper} sx={{ mt: 1, mb: 2 }}>
            <Table size="small">
              <TableBody>
                <StripedTableRow>
                  <StripedTableHeadCell>STUDENT:</StripedTableHeadCell>
                  <StripedTableCell>
                    {_selectedPass.student_last_name},{" "}
                    {_selectedPass.student_first_name}
                  </StripedTableCell>
                </StripedTableRow>
                <StripedTableRow>
                  <StripedTableHeadCell>Pass From:</StripedTableHeadCell>
                  <StripedTableCell>
                    {_selectedPass.teacher_last_name},{" "}
                    {_selectedPass.teacher_first_name}
                  </StripedTableCell>
                </StripedTableRow>
                <StripedTableRow>
                  <StripedTableHeadCell>Pass To:</StripedTableHeadCell>
                  <StripedTableCell>
                    {getPassDestinationText(_selectedPass)}
                  </StripedTableCell>
                </StripedTableRow>
                <StripedTableRow>
                  <StripedTableHeadCell>Created By:</StripedTableHeadCell>
                  <StripedTableCell>
                    {_selectedPass.created_by_last_name},{" "}
                    {_selectedPass.created_by_first_name} (
                    {getPriorityText(_selectedPass.created_by_priority)})
                  </StripedTableCell>
                </StripedTableRow>
                <StripedTableRow>
                  <StripedTableHeadCell>Approved At:</StripedTableHeadCell>
                  <StripedTableCell>
                    {dayjs(_selectedPass.approved_at).format("MM/DD hh:mm a")}
                  </StripedTableCell>
                </StripedTableRow>
                <StripedTableRow>
                  <StripedTableHeadCell>Comment:</StripedTableHeadCell>
                  <StripedTableCell>
                    <div
                      dangerouslySetInnerHTML={{
                        __html: getCommentText(_selectedPass),
                      }}
                    />
                  </StripedTableCell>
                </StripedTableRow>
              </TableBody>
            </Table>
          </TableContainer>
        </DialogContent>

        <DialogActions>
          <Button onClick={handlePassDetailsClose} variant="outlined">
            Close
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        onClose={handlePassHistoryClose}
        open={_passHistoryOpen}
        PaperProps={{
          sx: {
            width: 800,
            maxHeight: 700,
          },
        }}
      >
        <DialogTitle>
          Pass History for {_selectedPass.student_first_name}{" "}
          {_selectedPass.student_last_name}
        </DialogTitle>

        <DialogContent>
          <TableContainer component={Paper} sx={{ mt: 1, mb: 2 }}>
            <Table size="small">
              <TableHead>
                <StripedTableRow>
                  <StripedTableCell>Created</StripedTableCell>
                  <StripedTableCell>From/To</StripedTableCell>
                  <StripedTableCell>Status</StripedTableCell>
                  <StripedTableCell>Created By</StripedTableCell>
                  <StripedTableCell>Approved</StripedTableCell>
                </StripedTableRow>
              </TableHead>
              <TableBody>
                <TableRowNoResults data={_selectedStudentPasses} />
                {_selectedStudentPasses.map((pass) => (
                  <StripedTableRow>
                    <StripedTableCell>
                      {dayjs(pass.dateCreated).format("MM/DD hh:mm")}
                    </StripedTableCell>
                    <StripedTableCell>
                      {formatPassFromTo(pass)}
                    </StripedTableCell>
                    <StripedTableCell>
                      {getPassStatusText(pass.status)}
                    </StripedTableCell>
                    <StripedTableCell>
                      <>
                        {pass.createdByLastName},{" "}
                        {pass.createdByFirstName.substring(0, 1)} (
                        {getPriorityText(pass.createdByPriority)})
                      </>
                    </StripedTableCell>
                    <StripedTableCell>
                      {pass.approvedAt == null && <>N/A</>}
                      {pass.approvedAt != null && (
                        <>{dayjs(pass.approvedAt).format("MM/DD hh:mm")}</>
                      )}
                    </StripedTableCell>
                  </StripedTableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </DialogContent>

        <DialogActions>
          <Button onClick={handlePassHistoryClose}>Close</Button>
        </DialogActions>
      </Dialog>

      <Dialog
        onClose={handleStudentCommentClose}
        open={_studentCommentOpen}
        PaperProps={{
          sx: {
            height: 500,
          },
        }}
      >
        <DialogTitle>Add Student Comment</DialogTitle>

        <DialogContent>
          <InputLabel sx={{ mt: 0 }}>Start and End Dates *:</InputLabel>
          <MobileDateRangePicker
            value={_dateRange}
            sx={{ mt: 1, mb: 2 }}
            onChange={handleChangeDateRange}
          />
          <StudentsSelector
            school={_school}
            jwt={_jwt}
            onChange={(studentEids) => setSelectedStudentEids(studentEids)}
            selectedStudents={_selectedStudentEids}
          />{" "}
          <InputLabel sx={{ mt: 1 }}>Select Comment *:</InputLabel>
          <SingleSelect
            sx={{ mb: 1 }}
            fullWidth
            size="small"
            onChange={(evt) => setSchoolCommentId(evt.target.value)}
            value={_schoolCommentId}
          >
            {_metaSchoolComments.map((c, i) => (
              <SingleMenuItem key={i + 1} value={c.id}>
                {c.commentText}
              </SingleMenuItem>
            ))}
          </SingleSelect>
          <InputLabel sx={{ mt: 1 }}>Additional Details (optional):</InputLabel>
          <TextField
            margin="dense"
            type="text"
            fullWidth
            size="small"
            onChange={(event) => setCommentDetails(event.target.value)}
            placeholder="Optional Field"
            value={_commentDetails}
          />
        </DialogContent>

        <DialogActions>
          <Button onClick={handleStudentCommentClose}>Cancel</Button>
          <Button variant="contained" onClick={handleAddCommentOK}>
            Add Comment
          </Button>
        </DialogActions>
      </Dialog>

      {_timeOpen && (
        <UpdateScoreTimeDialog
          value={_updatedTime}
          onChange={(djValue) => {
            setUpdatedTime(djValue);
          }}
          onClose={handleTimeClose}
          onOk={onTimeClickOk}
        />
      )}
    </>
  );
};

export default AdminWelcome;
